import { useEffect, useRef, useState } from "react";
import { X, FileText, AlertTriangle } from "lucide-react";
import { useDocuments } from "../modal/DocumentsContext";
import type { SimilarDocumentMatch } from "../api/DocumentsApi";

interface Props {
  onClose: () => void;
  onCreated: (documentId: number) => void;
}

export default function CreateDocumentModal({ onClose, onCreated }: Props) {
  const { createDocument, categories, checkSimilarDocuments } = useDocuments();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState<number | "">("");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [similar, setSimilar] = useState<SimilarDocumentMatch[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!title.trim()) { setSimilar([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        setSimilar(await checkSimilarDocuments(title.trim()));
      } catch {
        // non-blocking — a failed similarity check should never obstruct creation
      }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [title, checkSimilarDocuments]);

  const canSubmit = title.trim().length > 0;

  function addTag() {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) setTags((prev) => [...prev, t]);
    setTagInput("");
  }

  function removeTag(t: string) {
    setTags((prev) => prev.filter((x) => x !== t));
  }

  async function confirm() {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const document = await createDocument({
        title: title.trim(),
        description: description.trim() || undefined,
        category_id: categoryId === "" ? undefined : categoryId,
        tags,
      });
      onCreated(document.id);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || "Failed to create document. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center">
              <FileText className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-base font-semibold text-foreground font-inter">New Document</h2>
          </div>
          <button onClick={onClose} disabled={saving} className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3 mb-4">
          <div>
            <label className="block text-xs font-medium text-muted-foreground font-inter mb-1.5">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Q3 Onboarding Guide"
              className="w-full bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter px-3 py-2 focus:outline-none focus:border-primary"
            />
          </div>

          {similar.length > 0 && (
            <div className="flex items-start gap-2 text-xs text-amber-400 font-inter bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <div className="space-y-1">
                <p>You may already have a similar document:</p>
                {similar.map((m) => (
                  <p key={m.id}>
                    <span className="font-medium">{m.title}</span> ({Math.round(m.ratio * 100)}% match)
                  </p>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-muted-foreground font-inter mb-1.5">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter px-3 py-2 focus:outline-none focus:border-primary resize-none"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground font-inter mb-1.5">Category (optional)</label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value === "" ? "" : Number(e.target.value))}
              className="w-full bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter px-3 py-2 focus:outline-none focus:border-primary"
            >
              <option value="">No category</option>
              {categories.filter((c) => c.is_active).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground font-inter mb-1.5">Tags (optional)</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                placeholder="Type a tag, press Enter"
                className="flex-1 bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter px-3 py-2 focus:outline-none focus:border-primary"
              />
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {tags.map((t) => (
                  <span key={t} className="flex items-center gap-1 text-[11px] bg-surface-muted border border-border/60 rounded-lg px-2 py-1 font-inter text-foreground">
                    {t}
                    <button onClick={() => removeTag(t)} className="text-muted-foreground hover:text-destructive">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {error && (
          <p className="text-xs text-destructive font-inter mb-4 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-xl transition-colors disabled:opacity-40 font-inter"
          >
            Cancel
          </button>
          <button
            onClick={confirm}
            disabled={saving || !canSubmit}
            className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-inter"
          >
            {saving ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}
